import { createReducer, on } from '@ngrx/store';
import { Order, OrdersTotalResponse } from 'graphql-api/baskets-api-interfaces';

import {
  loadFiltersSuccess,
  setActiveFilters,
  setSearchQuery,
} from '../filters/actions/filters.actions';
import { getUniqueOrderId } from '../shared/get-unique-order-id/get-unique-order-id';

import * as fromActions from './orders.actions';

export const ordersFeatureKey = 'orders';

export interface OrdersState {
  orders: {
    data: Order[] | null;
    totals: OrdersTotalResponse | null;
    loading: boolean;
    loaded: boolean;
    error: string | null;
    total: number;
    afterKey: string | null;
    paginationLoading: boolean;
  };
  exportOrders: {
    loading: boolean;
    error: string | null;
    exportingAllOrders: boolean;
  };
  selectedIds: string[];
}

export const initialState: OrdersState = {
  orders: {
    data: null,
    totals: null,
    loading: false,
    loaded: false,
    error: null,
    total: 0,
    afterKey: null,
    paginationLoading: false,
  },
  exportOrders: {
    exportingAllOrders: false,
    loading: false,
    error: null,
  },
  selectedIds: [],
};

export const reducer = createReducer<OrdersState>(
  initialState,
  on(fromActions.selectOrderId, (state, action) => ({
    ...state,
    selectedIds: [...state.selectedIds, action.id],
  })),
  on(fromActions.deselectOrderId, (state, action) => ({
    ...state,
    selectedIds: state.selectedIds.filter(
      (selectedId) => selectedId !== action.id,
    ),
  })),
  on(fromActions.loadOrders, (state) => ({
    ...state,
    orders: {
      ...state.orders,
      loading: true,
      error: null,
    },
  })),
  on(fromActions.loadOrdersSuccess, (state, { orders, totals, afterKey }) => ({
    ...state,
    orders: {
      ...state.orders,
      data: orders,
      totals,
      loading: false,
      loaded: true,
      error: null,
      afterKey,
    },
    selectedIds: state.selectedIds.filter((selectedId) =>
      orders.some((order) => getUniqueOrderId(order) === selectedId),
    ),
  })),
  on(fromActions.loadOrdersFailure, (state, { error }) => ({
    ...state,
    orders: {
      ...state.orders,
      loading: false,
      loaded: false,
      data: [],
      totals: null,
      error,
    },
  })),
  on(fromActions.exportOrders, (state) => ({
    ...state,
    exportOrders: {
      ...state.exportOrders,
      loading: true,
      error: null,
    },
  })),
  on(fromActions.exportOrdersSuccess, (state) => ({
    ...state,
    exportOrders: {
      ...state.exportOrders,
      loading: false,
      error: null,
    },
  })),
  on(fromActions.exportOrdersFailure, (state, { error }) => ({
    ...state,
    exportOrders: {
      ...state.exportOrders,
      loading: false,
      error,
    },
  })),
  on(fromActions.exportAllOrders, (state) => ({
    ...state,
    exportOrders: {
      ...state.exportOrders,
      exportingAllOrders: true,
      error: null,
    },
  })),
  on(fromActions.exportAllOrdersSuccess, (state) => ({
    ...state,
    exportOrders: {
      ...state.exportOrders,
      exportingAllOrders: false,
      error: null,
    },
  })),
  on(fromActions.exportAllOrdersFailure, (state, { error }) => ({
    ...state,
    exportOrders: {
      ...state.exportOrders,
      exportingAllOrders: false,
      error,
    },
  })),
  on(fromActions.resetOrdersState, () => ({ ...initialState })),
  on(loadFiltersSuccess, (state, { total }) => {
    return {
      ...state,
      orders: {
        ...state.orders,
        total,
      },
    };
  }),
  on(setActiveFilters, setSearchQuery, (state) => {
    return {
      ...state,
      orders: {
        ...state.orders,
        afterKey: null,
      },
    };
  }),
  on(fromActions.increaseOrderBookPageNumber, (state) => {
    return {
      ...state,
      orders: {
        ...state.orders,
        paginationLoading: true,
      },
    };
  }),
  on(
    fromActions.increasePageNumberSuccess,
    (state, { orders, afterKey }): OrdersState => {
      return {
        ...state,
        orders: {
          ...state.orders,
          data: Array.isArray(state.orders.data)
            ? state.orders.data?.concat(orders)
            : orders,
          paginationLoading: false,
          afterKey,
        },
      };
    },
  ),
  on(fromActions.increasePageNumberFailure, (state): OrdersState => {
    return {
      ...state,
      orders: {
        ...state.orders,
        paginationLoading: false,
      },
    };
  }),
);
